import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs } from "@mantine/core";

import { Container, LayoutStandard, Loader } from "components";
import { AuthorityConstants, RouteConstants, ServerConstants, ServiceConstants } from "constant";
import { CardService } from "services";
import { displayClasses } from "styles";
import { CardResponse } from "types";
import { AppUtils } from "utils";

import { IRootState } from "config/redux";
import CardHeader from "./header";
import CardPersonal from "./personal";
import { useForm } from "@mantine/form";
import CardAddress from "./address";
import CardEmployment from "./employment";
import CardCard from "./card";

const { ROUTE_ERROR_406 } = RouteConstants;
const { REQUEST_CARDS_VIEW_ID, REQUEST_PENDING } = ServiceConstants;
const { EXCEPTION_IDENTITY_NOT_FOUND } = ServerConstants;

const TABS = {
  PERSONAL: 'personal',
  ADDRESS: 'address',
  EMPLOYMENT: 'employment',
  CARD: 'card',
}

const CardViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARDS_VIEW_ID]);
  const [card, setCard] = useState<CardResponse | undefined>(undefined);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {}
  });

  useEffect(() => {
    CardService.cardsOne(id!);
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(cardServiceObject, REQUEST_CARDS_VIEW_ID,
      (val: CardResponse) => {
        setCard(val);
        form.setValues({
          ...val,
          firstName: val.user.fullName.split(' ')[0],
          lastName: val.user.fullName.split(' ')[1],
          phone: val.user.countryCode + ' ' + val.user.phoneNumber,
          email: val.user.email,
          birthDate: val.birthDate ? new Date(val.birthDate) : undefined,
          companyEmployedAt: val.companyEmployedAt ? new Date(val.companyEmployedAt) : undefined
        } as CardResponse)
      }, (err) => {
      if (err?.data?.code === EXCEPTION_IDENTITY_NOT_FOUND) {
        navigate(ROUTE_ERROR_406);
      }
    });
  }, [cardServiceObject]);

  const tabTitle = useMemo(() => {
    if (!card) {
      return AppUtils.composeTabTitle(t('card.view.title'));
    }
    return AppUtils.composeTabTitle(t('card.view.title')) + ' - ' + card.user.fullName;
  }, [card]);

  const isLoading = useMemo(() => cardServiceObject && cardServiceObject.status === REQUEST_PENDING,[cardServiceObject]);

  return (
    <LayoutStandard
      authority={AuthorityConstants.ROLE_CC_R}
      title={tabTitle}
      >
      <Container>
        {isLoading || !card ? <Loader /> :
        <>
          <CardHeader card={card!} t={t} />
          <Tabs defaultValue={TABS.PERSONAL} variant={'pills'}>
            <Tabs.List grow={true} >
              <Tabs.Tab value={TABS.PERSONAL} className={displayClasses.tabView}>{t('card.view.tab.' + TABS.PERSONAL)}</Tabs.Tab>
              <Tabs.Tab value={TABS.ADDRESS} className={displayClasses.tabView}>{t('card.view.tab.' + TABS.ADDRESS)}</Tabs.Tab>
              <Tabs.Tab value={TABS.EMPLOYMENT} className={displayClasses.tabView}>{t('card.view.tab.' + TABS.EMPLOYMENT)}</Tabs.Tab>
              <Tabs.Tab value={TABS.CARD} className={displayClasses.tabView}>{t('card.view.tab.' + TABS.CARD)}</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value={TABS.PERSONAL} className={displayClasses.tabContent}><CardPersonal form={form} t={t} /></Tabs.Panel>
            <Tabs.Panel value={TABS.ADDRESS} className={displayClasses.tabContent}><CardAddress form={form} t={t} /></Tabs.Panel>
            <Tabs.Panel value={TABS.EMPLOYMENT} className={displayClasses.tabContent}><CardEmployment form={form} t={t} /></Tabs.Panel>
            <Tabs.Panel value={TABS.CARD} className={displayClasses.tabContent}><CardCard form={form} t={t} /></Tabs.Panel>
          </Tabs>
        </>}
      </Container>
    </LayoutStandard>
  )
}

export default CardViewPage;